import { useQuery } from '@apollo/client';
import { useCallback, useEffect, useState } from 'react';

import { MAX_NUMBER_OF_NOTES, NOTES_MAX_RESULTS } from '../config/constants';
import { GET_NOTES_BY_GREASE_TRAP_ID } from '../services/graphql/notes/notesQuery';
import { calculateGreaseTrapNotesHasNextPage } from './useGreaseTrapNotes.helpers';

const useGreaseTrapNotes = ({ greaseTrapId }: { greaseTrapId: string }) => {
  const {
    loading,
    fetchMore,
    error: errorNotes,
    data,
    refetch,
    updateQuery,
  } = useQuery(GET_NOTES_BY_GREASE_TRAP_ID, {
    variables: {
      greaseTrapId,
      offset: 0,
      limit: NOTES_MAX_RESULTS,
    },
  });

  const [notes, setNotes] = useState(data?.getNotesByGreaseTrapId.data ?? []);

  const [totalNotes, setTotalNotes] = useState(
    data?.getNotesByGreaseTrapId.count || 0,
  );

  const [hasNextPage, setHasNextPage] = useState<boolean>(true);

  function refetchNotes() {
    setHasNextPage(true);
    setNotes([]);

    refetch({
      greaseTrapId,
      offset: 0,
      limit: NOTES_MAX_RESULTS,
    });
  }

  useEffect(() => {
    if (
      data?.getNotesByGreaseTrapId.data &&
      data?.getNotesByGreaseTrapId.data.length > 0
    ) {
      setNotes(data.getNotesByGreaseTrapId.data);
      setTotalNotes(data.getNotesByGreaseTrapId.count);
    }
  }, [data]);

  const fetchMoreNotes = useCallback(() => {
    if (notes.length === MAX_NUMBER_OF_NOTES) return;

    fetchMore({
      variables: {
        greaseTrapId,
        offset: notes.length,
        limit: NOTES_MAX_RESULTS,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;

        const newData = fetchMoreResult.getNotesByGreaseTrapId.data || [];
        setNotes((prevNotes: typeof notes) => [...prevNotes, ...newData]);

        setHasNextPage(
          calculateGreaseTrapNotesHasNextPage(
            fetchMoreResult.getNotesByGreaseTrapId.count || 0,
            NOTES_MAX_RESULTS,
            notes.length + newData.length,
          ),
        );

        return {
          getNotesByGreaseTrapId: {
            ...prev.getNotesByGreaseTrapId,
            data: [...prev.getNotesByGreaseTrapId.data, ...newData],
          },
        };
      },
    });
  }, [fetchMore, notes, greaseTrapId]);

  return {
    fetchMoreNotes,
    loading,
    notes,
    refetchNotes,
    errorNotes,
    hasNextPage,
    updateQuery,
    totalNotes,
  };
};

export default useGreaseTrapNotes;
